// FilterStrip — page-level schedule filter bar. Composes the locked
// Filter Chip primitive (it is NOT a new chip): a contracts picker
// (chip + popover with search / select-all / clear-all / checkbox
// list), six activity-type toggle chips each carrying a ≤8px
// type-colour dot (R1 — a bare type dot only appears here and in the
// legend), a single-select status cycle (All → Overdue → Due this
// week → Pending decisions), a debounced search input, and a Reset
// control that is only visible when at least one filter differs from
// default. Controlled/presentational — the page owns the URL state.
//
// Status bucket vocabulary + labels + cycle order are imported from
// scheduleBuckets so StatsRow / FilterStrip / Schedule.tsx cannot drift
// on bucket slug spellings.
//
// Visual treatment mirrors the Insurances FilterChipBar (light-by-
// default, dark overrides) so the schedule view reads in a light app
// theme. Earlier dark-only treatment shipped invisible against a light
// background.

import { useEffect, useRef, useState } from "react";
import { Search, ChevronDown } from "lucide-react";
import type { ActivityKind } from "../../../types/schedule";
import {
  SCHEDULE_BUCKET_CYCLE,
  SCHEDULE_BUCKET_LABEL,
  type ScheduleStatusBucket,
} from "./scheduleBuckets";
import {
  KIND_DOT_BG,
  KIND_LABEL_PLURAL,
  SCHEDULE_KINDS,
} from "./kindVocabulary";

export interface ScheduleContractOption {
  id: string;
  name: string;
  ref: string;
}

// Default state = no contracts filter, no kinds hidden, no status,
// empty search. Reset is shown when ANY of these differs.
const DEFAULT_HIDDEN: string[] = [];

function isAtDefault(
  selectedContracts: string[] | undefined,
  hiddenKinds: string[],
  status: ScheduleStatusBucket | undefined,
  search: string,
): boolean {
  if (selectedContracts && selectedContracts.length > 0) return false;
  if (status !== undefined) return false;
  if (search.trim() !== "") return false;
  const sortedHidden = [...hiddenKinds].sort();
  const sortedDefault = [...DEFAULT_HIDDEN].sort();
  return (
    sortedHidden.length === sortedDefault.length &&
    sortedHidden.every((k, i) => k === sortedDefault[i])
  );
}

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";
const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";

const POPOVER_CLASS =
  "absolute z-20 mt-1 max-h-[60vh] w-72 overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

const SEARCH_DEBOUNCE_MS = 200;

export interface FilterStripProps {
  contractOptions: ScheduleContractOption[];
  selectedContracts: string[] | undefined;
  hiddenKinds: string[];
  status: ScheduleStatusBucket | undefined;
  search: string;
  onContractsChange: (next: string[] | undefined) => void;
  onToggleKind: (kind: ActivityKind) => void;
  onStatusChange: (next: ScheduleStatusBucket | undefined) => void;
  onSearchChange: (next: string) => void;
  onReset: () => void;
}

export default function FilterStrip({
  contractOptions,
  selectedContracts,
  hiddenKinds,
  status,
  search,
  onContractsChange,
  onToggleKind,
  onStatusChange,
  onSearchChange,
  onReset,
}: FilterStripProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [contractSearch, setContractSearch] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);

  // Local search mirrors the input for responsive typing; the
  // debounced timer dispatches to the parent after a pause. Resyncs
  // from the prop so a parent-driven reset flows back into the input.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });
  const [localSearch, setLocalSearch] = useState(search);
  const [prevSearch, setPrevSearch] = useState(search);
  if (search !== prevSearch) {
    setPrevSearch(search);
    setLocalSearch(search);
  }
  // Cancel any in-flight debounce when the search prop changes so a
  // stale-typed value can't land after a parent-driven reset (e.g. Clear all).
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, [search]);
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!pickerOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node)
      ) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [pickerOpen]);

  const handleSearch = (value: string) => {
    setLocalSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChangeRef.current(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const selectedSet = new Set(selectedContracts ?? []);
  const hiddenSet = new Set(hiddenKinds);

  const handleCycleStatus = () => {
    const idx = SCHEDULE_BUCKET_CYCLE.findIndex((s) => s === status);
    const next = SCHEDULE_BUCKET_CYCLE[
      (idx + 1) % SCHEDULE_BUCKET_CYCLE.length
    ];
    onStatusChange(next);
  };

  const toggleContract = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const arr = [...next];
    onContractsChange(arr.length > 0 ? arr : undefined);
  };

  const filteredOptions = contractOptions.filter((c) => {
    if (!contractSearch.trim()) return true;
    const q = contractSearch.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) || c.ref.toLowerCase().includes(q)
    );
  });

  const selectedCount = selectedContracts?.length ?? contractOptions.length;
  const showReset = !isAtDefault(
    selectedContracts,
    hiddenKinds,
    status,
    search,
  );

  return (
    <div className="flex flex-col gap-2">
      {/* Row 1: chip groups with leading uppercase labels. The label
          sectioning matches wireframe-2 so the strip reads as three
          discrete filter groups (contracts / show / status) rather
          than a flat chip soup. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2">
          <GroupLabel>Contracts</GroupLabel>
          {/* Contracts picker — the chip text is just "N of M ▾" now
              that the group label sits outside. */}
          <div ref={pickerRef} className="relative">
            <button
              type="button"
              data-testid="filter-contracts-trigger"
              onClick={() => setPickerOpen((o) => !o)}
              aria-haspopup="dialog"
              aria-expanded={pickerOpen}
              aria-label={`Contracts · ${selectedCount} of ${contractOptions.length}`}
              className={`${CHIP_BASE} ${CHIP_INACTIVE}`}
            >
              <span>
                {selectedCount} of {contractOptions.length}
              </span>
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </button>
        {pickerOpen && (
          <div
            data-testid="filter-contracts-popover"
            role="dialog"
            aria-label="Select contracts"
            className={POPOVER_CLASS}
          >
            <input
              type="search"
              aria-label="Search contracts"
              placeholder="Search contracts…"
              value={contractSearch}
              onChange={(e) => setContractSearch(e.target.value)}
              className="mb-2 h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-caption text-slate-700 focus:border-brand-400 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
            />
            <div className="mb-2 flex gap-2">
              <button
                type="button"
                onClick={() =>
                  onContractsChange(contractOptions.map((c) => c.id))
                }
                className="text-caption text-brand-600 hover:underline dark:text-brand-400"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => onContractsChange(undefined)}
                className="text-caption text-slate-500 hover:underline dark:text-slate-400"
              >
                Clear all
              </button>
            </div>
            <ul className="space-y-1">
              {filteredOptions.map((c) => {
                const id = `filter-contract-${c.id}`;
                return (
                  <li key={c.id}>
                    <label
                      htmlFor={id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      <input
                        id={id}
                        type="checkbox"
                        checked={selectedSet.has(c.id)}
                        onChange={() => toggleContract(c.id)}
                        className="accent-brand-600"
                      />
                      <span className="font-medium">{c.name}</span>
                      <span className="font-mono text-caption text-slate-500 dark:text-slate-400">
                        {c.ref}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
          </div>
        </div>

        {/* SHOW group — activity-type chips */}
        <div className="flex flex-wrap items-center gap-2">
          <GroupLabel>Show</GroupLabel>
          {SCHEDULE_KINDS.map((kind) => {
            const active = !hiddenSet.has(kind);
            return (
              <button
                key={kind}
                type="button"
                data-testid={`filter-kind-${kind}`}
                onClick={() => onToggleKind(kind)}
                aria-pressed={active}
                className={`${CHIP_BASE} ${active ? CHIP_ACTIVE : CHIP_INACTIVE}`}
              >
                <span
                  aria-hidden="true"
                  className={`h-2 w-2 rounded-full ${KIND_DOT_BG[kind]}`}
                />
                <span>{KIND_LABEL_PLURAL[kind]}</span>
              </button>
            );
          })}
        </div>

        {/* STATUS group — single-select cycle. The visible chip text
            drops the redundant "Status:" prefix now that the group
            label carries the role. */}
        <div className="flex items-center gap-2">
          <GroupLabel>Status</GroupLabel>
          <button
            type="button"
            data-testid="filter-status-cycle"
            onClick={handleCycleStatus}
            className={`${CHIP_BASE} ${status ? CHIP_ACTIVE : CHIP_INACTIVE}`}
          >
            {status ? SCHEDULE_BUCKET_LABEL[status] : "All"}
          </button>
        </div>
      </div>

      {/* Row 2: search input + the conditional Reset filters action.
          The wireframe puts both on a dedicated second row so the chip
          groups above read as a clean filter palette and the textual
          controls live in their own band. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            aria-label="Search activities"
            placeholder="Search activities…"
            value={localSearch}
            onChange={(e) => handleSearch(e.target.value)}
            className="h-8 w-72 rounded-md border border-slate-300 bg-white pl-7 pr-3 text-caption text-slate-700 focus:border-brand-400 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
          />
        </div>

        {showReset && (
          <button
            type="button"
            data-testid="filter-reset"
            onClick={onReset}
            className="rounded-md px-3 py-1.5 text-caption font-semibold text-brand-600 hover:bg-brand-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400 dark:hover:bg-brand-950"
          >
            Reset filters
          </button>
        )}
      </div>
    </div>
  );
}

// Compact uppercase-overline label used as a group prefix for each
// filter section. Keeps the schedule strip visually consistent with
// the other /manage tooling labels.
function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-overline text-slate-500 dark:text-slate-400">
      {children}
    </span>
  );
}
